/**
 * Comment mark — highlights an arbitrary text selection and links it to a
 * DocumentCommentThread (matched by `commentId`, a thread's own id).
 *
 * Unlike the node-based extensions in this editor (citation, footnote…),
 * this is a plain Mark: it has no NodeView, so opening a thread from the
 * document is handled by a click listener on the editor's DOM (see
 * EditorCanvas.vue) rather than a Vue component per instance.
 *
 * Resolved vs. open is *not* distinguished visually in the text — every
 * commented range gets the same highlight; resolve/reopen state is only
 * shown in the side panel's thread list, to keep this mark's schema (and
 * the removeMark/re-add dance below) simple.
 */
import { Mark, mergeAttributes } from '@tiptap/core';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        comment: {
            /** Wrap the current selection with a comment highlight linked to `commentId`. */
            setComment: (commentId: string) => ReturnType;
            /** Remove every occurrence of a comment highlight (by thread id) from the document. */
            unsetComment: (commentId: string) => ReturnType;
        };
    }
}

export const CommentExtension = Mark.create({
    name: 'comment',

    // Typing right at the edge of a highlighted range shouldn't silently
    // extend it — matches how most annotation tools behave.
    inclusive: false,

    addAttributes() {
        return {
            commentId: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-comment-id'),
                renderHTML: (attrs) =>
                    attrs.commentId
                        ? { 'data-comment-id': attrs.commentId }
                        : {},
            },
        };
    },

    parseHTML() {
        return [{ tag: 'mark[data-comment-id]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'mark',
            mergeAttributes(HTMLAttributes, { class: 'comment-highlight' }),
            0,
        ];
    },

    addCommands() {
        return {
            setComment:
                (commentId: string) =>
                ({ chain }) =>
                    chain().setMark(this.name, { commentId }).run(),

            unsetComment:
                (commentId: string) =>
                ({ tr, dispatch }) => {
                    let found = false;

                    tr.doc.descendants((node, pos) => {
                        node.marks.forEach((mark) => {
                            if (
                                mark.type.name === 'comment' &&
                                mark.attrs.commentId === commentId
                            ) {
                                found = true;
                                tr.removeMark(
                                    pos,
                                    pos + node.nodeSize,
                                    mark.type,
                                );
                            }
                        });
                    });

                    if (found && dispatch) {
                        dispatch(tr);
                    }

                    return found;
                },
        };
    },
});
